/**
 * Property Workspace product page — BP-002.
 * Live UI screenshots captured from the running V3 app.
 */
import { useState } from "react";
import {
  Layers, FileText, Activity, Users2, Zap, Shield,
  Target, Brain, LineChart, ClipboardList, ScrollText, Hammer, Sparkles,
} from "lucide-react";
import ProductPageTemplate, { ProductScreen } from "../ProductPageTemplate";

// Real screenshots captured from /v3/properties/:id (see /app/frontend/public/marketing/workspace).
const IMG = (name) => `/marketing/workspace/${name}.png`;

// Hero uses the Deal Score screenshot (most visually striking, real 51/100 score).
const heroMockup = (
  <ProductScreen title="workspace / 1234 Any Street, Any City, USA 12345">
    <img
      src={IMG("deal_score")}
      alt="Live Property Workspace — Deal Score panel"
      className="w-full h-auto rounded-md"
      loading="eager"
      width="1600"
      height="1000"
    />
  </ProductScreen>
);

const SCREENS = [
  {
    slug: "deal-score",
    label: "Deal Score",
    icon: Target,
    caption: "51/100 · HIGH RISK · 5 category-weighted signals (BP-005 v1). Every score cites its gap and its source engine.",
    file: "deal_score",
  },
  {
    slug: "lead-score",
    label: "Lead Score",
    icon: Sparkles,
    caption: "Lead Scoring Intelligence (BP-lead-scoring) · 8 weighted signals · calibrated conversion likelihood · explainable next-best-actions.",
    file: "lead_score",
  },
  {
    slug: "strategy-compare",
    label: "Strategy Compare",
    icon: Layers,
    caption: "Balanced weighting · 8 exit strategies scored in parallel (BP-014) · Recalculate / Save / Publish workflow.",
    file: "strategy_compare",
  },
  {
    slug: "comparable-sales",
    label: "Comparable Sales",
    icon: LineChart,
    caption: "ARV Comps (BP-004) — DOM, distance, and adjustment audit trail per comp.",
    file: "comparable_sales",
  },
  {
    slug: "repair-estimate",
    label: "Repair Estimate",
    icon: Hammer,
    caption: "Room-by-room and system-by-system repair estimator (BP-003) with local labour rates.",
    file: "repair_estimate",
  },
  {
    slug: "deal-intelligence",
    label: "Deal Intelligence",
    icon: Brain,
    caption: "Assumptions engine · scenario names, ARV, repair cost, buyer rule %, target fee, cushion — deterministic calculators, versioned.",
    file: "deal_intelligence",
  },
  {
    slug: "marketing",
    label: "Marketing",
    icon: ClipboardList,
    caption: "Executive Marketing Dashboard (BP-018) · linear attribution · Revenue / Cost / ROI / ROAS per property.",
    file: "marketing",
  },
  {
    slug: "ai-recommendations",
    label: "AI Recommendations",
    icon: Brain,
    caption: "AI Acquisition Coach (BP-025) recommendations panel — grounded, confidence-capped, evidence-cited.",
    file: "ai_recommendations",
  },
  {
    slug: "activity-timeline",
    label: "Activity Timeline",
    icon: ScrollText,
    caption: "Immutable, hash-chained event log — every field change, message, calculation captured forever.",
    file: "activity_timeline",
  },
];

function WorkspaceScreensGallery() {
  const [active, setActive] = useState(SCREENS[0].slug);
  const current = SCREENS.find((s) => s.slug === active) || SCREENS[0];

  return (
    <section
      data-testid="workspace-screens-gallery"
      className="bg-slate-50 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">
              Live UI · not a mockup
            </div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
              Every tab is a plugin.<br />Every number cites its source.
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl">
              These are unretouched screenshots of the running app. Click a tab to inspect the actual UI your team lives in.
            </p>
          </div>
        </div>

        {/* Tab buttons */}
        <div
          className="mt-8 flex flex-wrap gap-2"
          role="tablist"
          data-testid="workspace-screens-tabs"
        >
          {SCREENS.map(({ slug, label, icon: Icon }) => {
            const isActive = slug === active;
            return (
              <button
                key={slug}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(slug)}
                data-testid={`workspace-tab-${slug}`}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-slate-900 text-white border-slate-900 shadow-md"
                    : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500 hover:text-emerald-700"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Screenshot frame */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
            <span className="ml-3 text-xs text-slate-500 font-mono truncate">
              iSpeedToClose · /v3/properties/1234-any-street · {current.label}
            </span>
          </div>
          <div className="p-3 md:p-4 bg-slate-50">
            <img
              key={current.slug}
              src={IMG(current.file)}
              alt={`Property Workspace — ${current.label} tab`}
              className="w-full h-auto rounded-md shadow-md"
              data-testid="workspace-screens-image"
              width="1600"
              height="1000"
            />
          </div>
        </div>

        <div className="mt-4 text-sm text-slate-600 max-w-3xl">
          <span className="font-semibold text-slate-900">{current.label}:</span>{" "}
          {current.caption}
        </div>
      </div>
    </section>
  );
}

export default function PropertyWorkspacePage() {
  return (
    <ProductPageTemplate
      slug="property-workspace"
      bp="BP-002 · Property Workspace"
      eyebrow="Product · Property Workspace"
      title="Every property is a"
      titleAccent="command center."
      subtitle="All information, documents, analyses, comps, communications, and activity live inside one workspace per property — surfaced through a plugin-based tab system so your team never leaves the deal to get the truth."
      bullets={[
        "Immutable timeline audits every decision and update",
        "Plugin tabs — Analysis, Comps, Docs, Messages, Coach — swap without page reloads",
        "Deep-links from every KPI into the deterministic engine that produced it",
      ]}
      heroMockup={heroMockup}
      postHero={<WorkspaceScreensGallery />}
      capabilities={[
        { icon: Layers, title: "Plugin Tab System", desc: "Analysis, Comps, Documents, Messages, Coach, Marketing, Disposition — mount only what the current stage needs." },
        { icon: Activity, title: "Immutable Timeline", desc: "Every field change, every message, every AI recommendation lives forever. Hash-chained for tamper evidence." },
        { icon: FileText, title: "Docs & Contracts", desc: "Upload, sign, share. DocuSign-ready. Every version, every signer captured on the timeline." },
        { icon: Users2, title: "Multi-Player", desc: "Comment threads, @mentions, assignment. Team members see each other's cursor state in the workspace." },
        { icon: Zap, title: "Command Palette", desc: "⌘K for actions — call seller, generate offer, run analysis, invoke Coach. Zero mouse-hunt." },
        { icon: Shield, title: "Governance-Ready", desc: "Row-level RBAC, retention policies, audit exports. Ready for institutional buyers and lenders." },
      ]}
      howItWorks={[
        { step: 1, title: "Ingest", desc: "New lead lands from Marketing, Buyers, or CRM → instantly gets a workspace." },
        { step: 2, title: "Enrich", desc: "Engines fire deterministically: ARV, Repair, Score, Strategy, Market Tier." },
        { step: 3, title: "Decide", desc: "AI Coach synthesises evidence into a Next-Best-Action with confidence + citations." },
        { step: 4, title: "Execute", desc: "Docs, calls, texts, offers — all fired from inside the workspace. Timeline captures everything." },
      ]}
      integrations={[
        { bp: "BP-003", name: "Repair Estimator" },
        { bp: "BP-004", name: "ARV Comps" },
        { bp: "BP-005", name: "Deal Score" },
        { bp: "BP-014", name: "Strategy Comparison" },
        { bp: "BP-024", name: "Market Intelligence" },
        { bp: "BP-025", name: "AI Acquisition Coach" },
        { bp: "BP-022", name: "Buyer Intelligence" },
        { bp: "BP-026", name: "Platform Governance" },
      ]}
      proof={{
        stat: "6×",
        label: "faster property intake",
        quote: "Our team went from 90 minutes per deal to under 15 — every number cited, every conversation captured, one page.",
        attribution: "Ops lead · Ohio wholesale team",
      }}
      related={[
        { title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "Grounded next-best-action for every workspace." },
        { title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "Score every exit strategy side-by-side inside the workspace." },
        { title: "Executive Dashboard", to: "/products/executive-dashboard", desc: "Roll every workspace into six-dimension business health." },
      ]}
    />
  );
}
